import * as React from 'react';
import { merge, uniqueId } from 'lodash-es';
import {
  type MasonryEngineNode,
  type MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';

import {
  Carousel,
  NoPeekCarousel,
} from '@leagueplatform/web-common-components';
import {
  type GDSHeadingLevel,
  type GDSSpace,
  ParagraphText,
  queryHelpers,
  StackItem,
  useMediaQuery,
} from '@leagueplatform/genesis-core';
import { BodyTextHtmlTagOptions } from '#utils/body-text-html-tag-options.ts';
import { useInvokeAction } from '../../hooks/use-invoke-action.hook';
import {
  COMPLEX_WIDGET_CARDS_WIDTH,
  COMPLEX_WIDGET_CARDS_WIDTH_MOBILE,
} from '../../constants';
import {
  MasonryDeeplinkAction,
  MasonryAnalyticsAction,
} from '../../types/masonry-driver-node-actions';
import { SectionHeader } from '../masonry-section-header-renderer/section-header.component';
import { formatClassNames } from '../../utils/format-masonry-class-name.util';

type MasonryCarouselNodeProperties = {
  heading?: string;
  headingLevel?: GDSHeadingLevel;
  body?: string;
  headerButtonLabel?: string;
  noPeek?: boolean;
  showProgressDots?: boolean;
};

export type MasonryCarouselNodeActions = {
  onHeaderButtonClick?: MasonryDeeplinkAction;
  onScrollLeft: MasonryAnalyticsAction;
  onScrollRight: MasonryAnalyticsAction;
};

export type MasonryCarouselNode = MasonryEngineNode<
  'carousel',
  MasonryCarouselNodeProperties,
  MasonryCarouselNodeActions,
  {
    items: MasonryEngineNode<string, any>[];
  }
>;

export type MasonryCarouselRendererProps =
  MasonryEngineNodeRendererProps<MasonryCarouselNode>;

export const MasonryCarouselRenderer = ({
  heading,
  headingLevel,
  body,
  headerButtonLabel,
  items,
  onHeaderButtonClick,
  onScrollLeft,
  onScrollRight,
  noPeek,
  className,
  engineClassName,
  showProgressDots,
  locatorId,
  css,
  rootElementRef,
  ...props
}: MasonryCarouselRendererProps) => {
  const invokeAction = useInvokeAction();
  const isMobile = useMediaQuery(queryHelpers.only('mobile'));

  const CarouselComponent = !noPeek ? Carousel : NoPeekCarousel;
  const headingId = React.useMemo(
    () => uniqueId('MasonryCarouselRenderer'),
    [],
  );

  if (!items) return null;

  const itemCount = React.Children?.count(items);

  return (
    <CarouselComponent
      ref={rootElementRef}
      data-testid={locatorId}
      className={formatClassNames(engineClassName, className)}
      name={heading || 'masonry-carousel'}
      heading={
        heading ? (
          <SectionHeader
            heading={heading}
            headingLevel={headingLevel}
            paddingTop="$none"
            {...{ id: headingId }}
          />
        ) : undefined
      }
      headingLabelledBy={headingId}
      description={
        <ParagraphText htmlStringOptions={BodyTextHtmlTagOptions}>
          {body}
        </ParagraphText>
      }
      // We need to assert that the value of 'oneAndHalf is a GDSSpace token
      // as NoPeekCarousel expects it and typescript isn't able to narrow the
      // type based on the value of noPeek
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...(!noPeek && { spaceBetweenCards: 'oneAndHalf' as GDSSpace })}
      showProgressDots={showProgressDots}
      linkCta={headerButtonLabel}
      linkUrl={onHeaderButtonClick?.payload.url}
      linkOnClick={(event: React.SyntheticEvent) => {
        if (onHeaderButtonClick) {
          invokeAction({
            event,
            ...onHeaderButtonClick,
          });
        }
      }}
      BackNavButtonAnalyticsFn={() => {
        if (onScrollLeft) {
          invokeAction({
            ...onScrollLeft,
          });
        }
      }}
      ForwardNavButtonAnalyticsFn={() => {
        if (onScrollRight) {
          invokeAction({
            ...onScrollRight,
          });
        }
      }}
      listStyles={{
        width: 'inherit',
        display: 'flex',
        minWidth: COMPLEX_WIDGET_CARDS_WIDTH,
        '@mobile': {
          minWidth: COMPLEX_WIDGET_CARDS_WIDTH_MOBILE,
        },
        ...css,
      }}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      {React.Children?.map(items, (child) => (
        <StackItem
          css={{
            width: '100%',
          }}
        >
          {React.cloneElement(child as unknown as React.ReactElement, {
            node: merge(
              {},
              (child as unknown as React.ReactElement).props.node,
              {
                properties: {
                  // Showing the child item in a concise UI by setting the property
                  // isChildItem to true if there are multiple items in the carousel
                  // or if the screen size is mobile. Otherwise, it will be shown
                  // as a full UI.
                  isChildItem: itemCount > 1 || isMobile,
                },
              },
            ),
          })}
        </StackItem>
      ))}
    </CarouselComponent>
  );
};
